import React, { useEffect, useState, useCallback } from "react";
import { api, formatDateIST } from "../lib/api";
import { useLookups } from "../lib/lookups";
import { Pill } from "../components/Pill";
import { SourceBadge } from "../components/SourceBadge";
import { LeadDetail } from "../components/LeadDetail";
import DateRangePicker from "../components/DateRangePicker";
import { Check, Clock, Phone, Mail, MessageCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BUCKETS = [
  { key: "overdue", label: "Overdue", accent: "#EF4444", headerBg: "#FEF2F2", headerText: "#991B1B", countBg: "#FCA5A5", testid: "bucket-overdue" },
  { key: "due_today", label: "Due Today", accent: "#F59E0B", headerBg: "#FFFBEB", headerText: "#92400E", countBg: "#FCD34D", testid: "bucket-due-today" },
  { key: "upcoming", label: "Upcoming", accent: "#6C47FF", headerBg: "#F5F3FF", headerText: "#4C1D95", countBg: "#C4B5FD", testid: "bucket-upcoming" },
];

export default function Followups() {
  const { CATEGORY_COLORS, STAGE_COLORS } = useLookups();
  const [data, setData] = useState({ overdue: [], due_today: [], upcoming: [], badge_count: 0 });
  const [active, setActive] = useState(null);
  const [range, setRange] = useState({ from: null, to: null });
  const [dialingId, setDialingId] = useState(null);

  const load = useCallback(async () => {
    const params = {};
    if (range.from) params.date_from = range.from;
    if (range.to) params.date_to = range.to;
    const { data } = await api.get("/reminders/buckets", { params });
    setData(data);
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const markDone = async (id) => { await api.post(`/leads/${id}/reminder/done`); toast.success("Marked done"); load(); };
  const snooze = async (id, days) => { await api.post(`/leads/${id}/reminder/snooze`, { days }); toast.success(`Snoozed +${days}d`); load(); };

  const placeCall = async (id) => {
    setDialingId(id);
    try {
      const { data } = await api.post(`/leads/${id}/mcube-call`);
      toast.success(data.mock ? "Call initiated (mock mode)" : "Call initiated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Call failed");
    } finally {
      setDialingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-[28px] font-[800] text-[#0D0B1F] leading-tight">Follow-ups</h1>
          <div className="text-[13px] text-[#8B90A0] mt-0.5">Track and action your lead check-ins</div>
        </div>
        <DateRangePicker value={range} onChange={setRange} testid="followups-range" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {BUCKETS.map((b) => {
          const items = data[b.key] || [];
          return (
            <div
              key={b.key}
              data-testid={b.testid}
              className="bg-white rounded-[14px] overflow-hidden flex flex-col"
              style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.07)", borderTop: `3px solid ${b.accent}` }}
            >
              {/* Column header */}
              <div className="flex items-center justify-between px-4 py-3" style={{ backgroundColor: b.headerBg }}>
                <div className="flex items-center gap-2">
                  <Clock className="h-[14px] w-[14px]" style={{ color: b.headerText }} />
                  <span className="text-[12px] font-[700]" style={{ color: b.headerText }}>{b.label}</span>
                </div>
                <span
                  className="text-[10px] font-[800] px-2 py-0.5 rounded-full text-white min-w-[22px] text-center"
                  style={{ backgroundColor: b.accent }}
                >{items.length}</span>
              </div>

              {/* Items */}
              <div className="flex-1 space-y-2 max-h-[calc(100vh-280px)] overflow-y-auto p-3">
                {items.map((l) => (
                  <div
                    key={l.id}
                    data-testid={`reminder-row-${l.id}`}
                    className="card p-3 card-hover"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <button
                        onClick={() => setActive(l)}
                        className="font-[700] text-[13px] text-[#0D0B1F] hover:text-[#6C47FF] text-left transition-colors"
                      >
                        {l.name}
                      </button>
                      <span className="text-[10px] text-[#8B90A0] whitespace-nowrap font-[500]">{formatDateIST(l.next_followup_at)}</span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                      <Pill color={CATEGORY_COLORS[l.category]}>{l.category}</Pill>
                      <Pill color={STAGE_COLORS[l.stage]}>{l.stage}</Pill>
                      <SourceBadge source={l.source} />
                    </div>
                    {l.followup_note && (
                      <div className="text-[11px] text-[#8B90A0] mt-2 line-clamp-2 leading-relaxed">{l.followup_note}</div>
                    )}
                    <div className="flex items-center gap-1.5 mt-3">
                      {l.phone && (
                        <button
                          onClick={() => placeCall(l.id)}
                          disabled={dialingId === l.id}
                          className="p-1.5 rounded-[6px] bg-[#EEE9FF] text-[#6C47FF] hover:bg-[#6C47FF] hover:text-white transition-colors disabled:opacity-50"
                          title="Call via MCube"
                        >
                          {dialingId === l.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Phone className="h-3 w-3" />}
                        </button>
                      )}
                      {l.phone && (
                        <a href={`https://wa.me/${l.phone.replace(/\D/g, "")}`} target="_blank" rel="noreferrer"
                          className="p-1.5 rounded-[6px] bg-[#DCFCE7] text-[#166534] hover:bg-[#166534] hover:text-white transition-colors">
                          <MessageCircle className="h-3 w-3" />
                        </a>
                      )}
                      {l.email && (
                        <a href={`mailto:${l.email}`} className="p-1.5 rounded-[6px] bg-[#EEE9FF] text-[#6C47FF] hover:bg-[#6C47FF] hover:text-white transition-colors">
                          <Mail className="h-3 w-3" />
                        </a>
                      )}
                      <div className="ml-auto flex items-center gap-1">
                        <button onClick={() => markDone(l.id)} data-testid={`done-${l.id}`}
                          className="p-1.5 rounded-[6px] bg-[#166534] text-white hover:bg-[#14532d] transition-colors" title="Done">
                          <Check className="h-3 w-3" />
                        </button>
                        <button onClick={() => snooze(l.id, 1)} data-testid={`snooze1-${l.id}`}
                          className="px-2 py-1 rounded-[6px] border border-[#E8EAEF] text-[10px] font-[700] text-[#0D0B1F] hover:border-[#6C47FF] hover:text-[#6C47FF] transition-colors">
                          +1d
                        </button>
                        <button onClick={() => snooze(l.id, 3)} data-testid={`snooze3-${l.id}`}
                          className="px-2 py-1 rounded-[6px] border border-[#E8EAEF] text-[10px] font-[700] text-[#0D0B1F] hover:border-[#6C47FF] hover:text-[#6C47FF] transition-colors">
                          +3d
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                {items.length === 0 && (
                  <div className="text-[12px] text-[#8B90A0] text-center py-10">Nothing here</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <LeadDetail lead={active} open={!!active} onClose={() => setActive(null)} onUpdated={(u) => { setActive(u); load(); }} />
    </div>
  );
}
